import PropTypes from 'prop-types';
import React, { useMemo, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFormikContext } from 'formik';
import { Skeleton, Alert } from '@material-ui/lab';
import classNames from 'classnames';
import { DialogContent } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Tooltip from '@mui/material/Tooltip';
import InfoIcon from '@material-ui/icons/InfoSharp';
import { DateField, NumberTextField } from '~/modules/common/components';
import { RoleDropdownRow } from '~/modules/resourcing/components/AllocateResourcePopup/RoleDropDownRow.js';
import UserAvailabilityDropdown from '~/modules/common/components/UserAvailabilityDropdown/UserAvailabilityDropdown';
import { mapIsoStringtoUtcObject } from '~/modules/common/dates/convert';
import { InputFieldAdornment } from '~/modules/resourcing/common/components/ResourceRequestDrawer/ResourceRequestDrawerDetails/ResourceDrawerAdornment';
import { getReactNumberFormatFromMe } from '~/modules/common/numbers';
import { useMeContext } from '~/modules/me';
import { getDefaultCustomPopper } from '~/modules/common/components/CustomPopper';
import { CreatableProjectDropdown } from '~/modules/common/components/CreatableProjectDropdown';
import AllocationByPeriod from '~/modules/resourcing/common/components/AllocationByPeriod/AllocationByPeriod';
import useAllocateResourcePeriodError from './hooks/useAllocateResourcePeriodError';
import { usePutAllocateResourceDialogSettings } from './hooks/useAllocateResourceDialogSettings';
import { useAllocateResourcePopupFormChange } from './hooks/useAllocateResourcePopupHandler';
import { useOnChangeHandlers, FIELDS } from './hooks/useOnChangeHandlers';
import { useRoleStyles, useInputElementStyles } from './useStyles';

const useStyles = makeStyles(theme => ({
  resourceDropdown: {
    padding: theme.spacing(3, 1, 1, 1),
    margin: theme.spacing(-2, 1, 1, 1)
  },
  dateRow: {
    display: 'flex',
    flexDirection: 'row',
    padding: theme.spacing(0, 1, 1, 1),
    margin: theme.spacing(0, 1, 1, 1)
  },
  loading: {
    width: '100%',
    padding: theme.spacing(0, 1.5, 0, 0)
  },
  allocated: {
    width: '100%',
    padding: theme.spacing(0, 1, 0, 0.5)
  },
  underloaded: {
    color: theme.palette.error.dark
  },
  alertMessage: {
    padding: theme.spacing(0, 2, 0, 2),
    '& .MuiAlert-message': {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5)
    }
  },
  helpIcon: {
    color: theme.palette.text.secondary
  },
  addProjectDropdown: {
    padding: theme.spacing(0, 2, 2, 2)
  },
  startDateField: {
    padding: theme.spacing(0, 1.5, 0, 0),
    width: '100%'
  },
  endDateField: {
    padding: theme.spacing(0, 1, 0, 0.5),
    width: '100%'
  },
  allocationRow: {
    display: 'flex',
    flexDirection: 'column',
    margin: theme.spacing(0, 2),
    minWidth: theme.spacing(60),
    gap: theme.spacing(1)
  }
}));

const useDilaogContentStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(0)
  }
}));

const endAdornment = <InputFieldAdornment adornmentSymbol="%" />;
const InputLabelPropsOverride = { shrink: true };

const getFieldLabels = formatMessage => ({
  role: formatMessage({ id: 'resourceRequestTitle.role' }),
  anyRole: formatMessage({ id: 'resourceRequestTitle.anyRole' }),
  selectResource: formatMessage({ id: 'addResource.selectResource' }),
  moreOptionsUser: formatMessage({
    id: 'moreOptions.users'
  }),
  totalAllocatedHours: formatMessage({
    id: 'allocateResourcesContent.totalAllocatedHours'
  }),
  loading: formatMessage({
    id: 'allocateResourcesContent.loading'
  }),
  minValueForLoad: formatMessage(
    {
      id: 'resourceRequestDrawerDetails.minValueForLoad'
    },
    {
      value: 0
    }
  ),
  startDate: formatMessage({
    id: 'allocateResourcesContent.startDate'
  }),
  endDate: formatMessage({
    id: 'allocateResourcesContent.endDate'
  }),
  alertTooltipMessage: formatMessage({
    id: 'addResource.alertTooltipMessage'
  })
});

const LoadingSkeleton = () => <Skeleton height={36} width={198} />;

const AllocateResourceDialogContent = ({
  existingResourceAllocation,
  defaultUser,
  scale,
  setExistingResourceAllocation,
  totalScheduledHours,
  showAddProject,
  showAddUser,
  setIsFormDirty,
  fetchProjectDetails,
  isLoading,
  setIsLoading,
  focusField,
  setFocusField,
  useResourceAllocationsV2Query
}) => {
  const classes = useStyles();
  const dialogContenClasses = useDilaogContentStyles();
  const inputElementClasses = useInputElementStyles();
  const roleClasses = useRoleStyles();
  const me = useMeContext();
  const { decimalScale } = getReactNumberFormatFromMe(me);
  const { formatMessage } = useIntl();
  const fieldLabels = getFieldLabels(formatMessage);
  const {
    values,
    dirty,
    setFieldValue,
    setFieldTouched,
    setValues
  } = useFormikContext();

  const [localTotalScheduledHours, setLocalTotalScheduledHours] = useState(
    totalScheduledHours
  );
  const isPsaRmpTaskAllocation1And2025Q3WcagEnabled = true;

  const {
    putAllocateResourceDialogSettings
  } = usePutAllocateResourceDialogSettings();

  const {
    onStartDateUpdate,
    onEndDateUpdate,
    onTotalAllocatedHoursUpdate,
    onLoadingUpdate,
    onUserUpdateForTaskAllocation: onUserUpdate,
    onRoleUpdate,
    onProjectUpdate,
    onSelectedAllocationPeriodChange,
    onDisplayUnitChange,
    onHoursValueChange,
    onPercentageValueChange
  } = useAllocateResourcePopupFormChange({
    setFieldValue,
    setFieldTouched,
    scale,
    setValues,
    values,
    fetchProjectDetails,
    totalScheduledHours: localTotalScheduledHours,
    setTotalScheduledHours: setLocalTotalScheduledHours,
    setExistingResourceAllocation,
    isPsaRmpTaskAllocation1Enabled: true,
    putAllocateResourceDialogSettings,
    useResourceAllocationsV2Query
  });

  const {
    onStartDateChange,
    onEndDateChange,
    onUserChange,
    onProjectChange
  } = useOnChangeHandlers(
    { onStartDateUpdate, onEndDateUpdate, onUserUpdate, onProjectUpdate },
    setIsLoading,
    setFocusField,
    isPsaRmpTaskAllocation1And2025Q3WcagEnabled
  );

  const {
    totalAllocatedHours,
    startDate,
    endDate,
    load,
    role,
    user,
    project,
    showAdvancedSettings,
    selectedAllocationPeriod,
    hoursForEntirePeriod,
    percentageForEntirePeriod,
    hoursPerMonth,
    hoursPerWeek
  } = values;

  const dateRange = useMemo(
    () => ({
      startDate: mapIsoStringtoUtcObject(startDate),
      endDate: mapIsoStringtoUtcObject(endDate)
    }),
    [endDate, startDate]
  );

  const loadingFieldHelperText = useMemo(
    () => (
      <>
        {load <= 0 && (
          <span className={classes.underloaded} role="alert">
            {fieldLabels.minValueForLoad}
          </span>
        )}
      </>
    ),
    [classes.underloaded, fieldLabels.minValueForLoad, load]
  );

  const {
    allocationError,
    entirePeriodDisabled
  } = useAllocateResourcePeriodError();

  useEffect(() => {
    setIsFormDirty(dirty);
  }, [dirty, setIsFormDirty]);

  return (
    <DialogContent classes={dialogContenClasses}>
      {showAddProject && (
        <div className={classes.addProjectDropdown}>
          {isLoading && <Skeleton height={56} width="100%" />}
          {!isLoading && (
            <CreatableProjectDropdown
              onChange={onProjectChange}
              classes={inputElementClasses}
              placeholder={formatMessage({
                id: 'quickAllocation.selectProject'
              })}
              label={formatMessage({ id: 'quickAllocation.project' })}
              variant="filled"
              dataQeId="AddProjectDropdown"
              value={project || null}
              name="project"
              customPopperComponent={getDefaultCustomPopper(412, 500)}
              InputLabelPropsOverride={InputLabelPropsOverride}
              autoFocus={focusField === FIELDS.PROJECT_FIELD}
            />
          )}
        </div>
      )}
      <div className={classes.dateRow}>
        <div className={classes.startDateField}>
          {isLoading ? (
            <Skeleton height={36} width="100%" />
          ) : (
            <DateField
              variant="standard"
              data-qe-id="AllocateResourceStartDate"
              label={fieldLabels.startDate}
              ariaLabel={fieldLabels.startDate}
              name="startDate"
              value={startDate}
              onChange={onStartDateChange}
              editable
              clearable={false}
              useV5DatePicker
              autoFocusOpenPickerButton={focusField === FIELDS.STARTDATE_FIELD}
            />
          )}
        </div>
        <div className={classes.endDateField}>
          {isLoading ? (
            <Skeleton height={36} width="100%" />
          ) : (
            <DateField
              variant="standard"
              data-qe-id="AllocateResourceEndDate"
              label={fieldLabels.endDate}
              ariaLabel={fieldLabels.endDate}
              name="endDate"
              value={endDate}
              onChange={onEndDateChange}
              editable
              clearable={false}
              useV5DatePicker
              autoFocusOpenPickerButton={focusField === FIELDS.ENDDATE_FIELD}
            />
          )}
        </div>
      </div>
      {showAddUser && (
        <RoleDropdownRow
          value={role || null}
          classes={roleClasses}
          onChange={onRoleUpdate}
          label={fieldLabels.role}
          placeholder={role ? '' : fieldLabels.anyRole}
          isLoading={isLoading}
          skeletonWidth="100%"
        />
      )}

      {showAddUser && (
        <div className={classNames(classes.resourceDropdown)}>
          {isLoading ? (
            <Skeleton height={56} width="100%" />
          ) : (
            <UserAvailabilityDropdown
              dataQeId="UserAvailabilityDropdown"
              dateRange={dateRange}
              onChange={onUserChange}
              value={user || null}
              userRole={role || null}
              placeholder={user ? '' : fieldLabels.selectResource}
              variant="filled"
              optionTypeText={fieldLabels.moreOptionsUser}
              hiddenLabel={false}
              disableOnBlur
              popperMinWidth={412}
              autoFocus={focusField === FIELDS.USER_FIELD}
            />
          )}
        </div>
      )}
      {showAdvancedSettings ? (
        isLoading ? (
          <div className={classes.allocationRow}>
            <Skeleton height={48} width="100%" />
            <Skeleton height={56} width="100%" />
          </div>
        ) : (
          <AllocationByPeriod
            dateRange={dateRange}
            selectedAllocationPeriod={selectedAllocationPeriod}
            hoursForEntirePeriod={hoursForEntirePeriod}
            percentageForEntirePeriod={percentageForEntirePeriod}
            hoursPerMonth={hoursPerMonth}
            hoursPerWeek={hoursPerWeek}
            onHoursValueChange={onHoursValueChange}
            onPercentageValueChange={onPercentageValueChange}
            onSelectedAllocationPeriodChange={onSelectedAllocationPeriodChange}
            error={allocationError}
            entirePeriodDisabled={entirePeriodDisabled}
            displayUnit={values.displayUnit}
            onDisplayUnitChange={onDisplayUnitChange}
          />
        )
      ) : (
        <div className={classes.dateRow}>
          <div className={classes.loading}>
            {isLoading ? (
              <LoadingSkeleton />
            ) : (
              <NumberTextField
                disabled={!user}
                fullWidth
                align="left"
                label={fieldLabels.loading}
                onChange={onLoadingUpdate}
                value={parseFloat(load.toFixed(decimalScale))}
                ariaLabel={fieldLabels.loading}
                min={0}
                max={999}
                endAdornment={endAdornment}
                precision={decimalScale}
                variant="standard"
                step="any"
                helperText={loadingFieldHelperText}
              />
            )}
          </div>
          <div className={classes.allocated}>
            {isLoading ? (
              <LoadingSkeleton />
            ) : (
              <NumberTextField
                disabled={!user}
                hiddenLabel
                fullWidth
                align="center"
                onChange={onTotalAllocatedHoursUpdate}
                value={parseFloat(totalAllocatedHours.toFixed(decimalScale))}
                ariaLabel={fieldLabels.totalAllocatedHours}
                label={fieldLabels.totalAllocatedHours}
                min={0}
                max={99999}
                precision={decimalScale}
                variant="standard"
              />
            )}
          </div>
        </div>
      )}
      {existingResourceAllocation && (
        <div className={classes.alertMessage}>
          <Alert severity="info" icon={false}>
            <FormattedMessage id="addResource.alertMessage" />
            <Tooltip
              title={fieldLabels.alertTooltipMessage}
              enterTouchDelay={0}
            >
              <InfoIcon className={classes.helpIcon} tabIndex={0} />
            </Tooltip>
          </Alert>
        </div>
      )}
    </DialogContent>
  );
};

AllocateResourceDialogContent.propTypes = {
  existingResourceAllocation: PropTypes.object,
  defaultUser: PropTypes.object,
  scale: PropTypes.string,
  setExistingResourceAllocation: PropTypes.func,
  totalScheduledHours: PropTypes.number,
  showAddUser: PropTypes.bool,
  showAddProject: PropTypes.bool,
  setIsFormDirty: PropTypes.func,
  fetchProjectDetails: PropTypes.func,
  isLoading: PropTypes.bool,
  setIsLoading: PropTypes.func,
  focusField: PropTypes.string,
  setFocusField: PropTypes.func,
  useResourceAllocationsV2Query: PropTypes.bool
};

export default AllocateResourceDialogContent;
